import { useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { apiRequest, type ApiError } from "@/lib/api"
import type { Segment, Workflow } from "@/types"
import { Label } from "@/components/ui/label"
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  Trash2,
  Loader2,
  Clock,
  Send,
  CheckCircle2,
  MousePointerClick,
  Power,
  PowerOff,
  GripVertical,
  ExternalLink,
  BarChart3,
  Pencil,
  ImagePlus,
  X,
  CalendarDays,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  COPY_LANGUAGES,
  DEFAULT_COPY_LANGUAGE,
  DEFAULT_TIMEZONE,
  RECURRING_TIMEZONES,
  copyLanguageLabel,
  timezoneAbbreviation,
  type CopyLanguage,
  type RecurringTimezone,
} from "@/lib/recurring-broadcast-options"

type DispatchMetrics = {
  sent: number
  delivered: number
  clicked: number
  total: number
}

type Dispatch = {
  id: number
  dispatch_number: number | null
  title: string | null
  body: string | null
  url: string | null
  image: string | null
  status: string
  started_at: string
  metrics: DispatchMetrics
}

type HistoryDay = {
  date: string
  dispatches: Dispatch[]
}

type ScheduledCopy = {
  title: string
  body: string
}

type AggregateMetrics = {
  total_dispatches: number
  total_messages: number
  sent: number
  delivered: number
  clicked: number
  delivery_rate: number
  click_rate: number
}

type HistoryResponse = {
  workflow: Workflow
  scheduled_copies: ScheduledCopy[]
  history: HistoryDay[]
  aggregate_metrics: AggregateMetrics
}

const AVAILABLE_HOURS = Array.from({ length: 24 }, (_, i) => i)

const RecurringBroadcastDetail = () => {
  const { projectSlug, workflowId } = useParams<{ projectSlug: string; workflowId: string }>()
  const queryClient = useQueryClient()

  const [isAddCopyOpen, setIsAddCopyOpen] = useState(false)
  const [newCopyTitle, setNewCopyTitle] = useState("")
  const [newCopyBody, setNewCopyBody] = useState("")

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editHours, setEditHours] = useState<number[]>([])
  const [editUrls, setEditUrls] = useState<string[]>([])
  const [editNewUrl, setEditNewUrl] = useState("")
  const [editEndDate, setEditEndDate] = useState("")
  const [editImages, setEditImages] = useState<{ url: string; filename: string }[]>([])
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [editTargetType, setEditTargetType] = useState<"all" | "segment">("all")
  const [editSegmentIds, setEditSegmentIds] = useState<number[]>([])
  const [editTimezone, setEditTimezone] = useState<RecurringTimezone>(DEFAULT_TIMEZONE)
  const [editCopyLanguage, setEditCopyLanguage] = useState<CopyLanguage>(DEFAULT_COPY_LANGUAGE)

  const basePath = `/api/projects/${encodeURIComponent(projectSlug || "")}/workflows/${workflowId}`

  const { data, isLoading, isError, error } = useQuery<HistoryResponse, ApiError>({
    queryKey: ["recurring-detail", projectSlug, workflowId],
    queryFn: () => apiRequest<HistoryResponse>(`${basePath}/dispatch_history`),
    enabled: Boolean(projectSlug && workflowId),
    refetchInterval: 120000,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  })

  const segmentsQuery = useQuery<{ segments: Segment[] }, ApiError>({
    queryKey: ["segments", projectSlug],
    queryFn: () => apiRequest<{ segments: Segment[] }>(`/api/projects/${encodeURIComponent(projectSlug || "")}/segments`),
    enabled: Boolean(projectSlug),
  })
  const segments = segmentsQuery.data?.segments ?? []

  const workflow = data?.workflow
  const scheduledCopies = data?.scheduled_copies ?? []
  const history = data?.history ?? []
  const metrics = data?.aggregate_metrics

  const toggleStatusMutation = useMutation<{ workflow: Workflow }, ApiError, string>({
    mutationFn: (status) =>
      apiRequest<{ workflow: Workflow }>(basePath, {
        method: "PATCH",
        body: JSON.stringify({ workflow: { status } }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-detail", projectSlug, workflowId] })
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
    },
  })

  const saveCopiesMutation = useMutation<{ scheduled_copies: ScheduledCopy[] }, ApiError, ScheduledCopy[]>({
    mutationFn: (copies) =>
      apiRequest<{ scheduled_copies: ScheduledCopy[] }>(`${basePath}/scheduled_copies`, {
        method: "PUT",
        body: JSON.stringify({ scheduled_copies: copies }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-detail", projectSlug, workflowId] })
    },
  })

  const updateConfigMutation = useMutation<{ workflow: Workflow }, ApiError, Record<string, unknown>>({
    mutationFn: (payload) =>
      apiRequest<{ workflow: Workflow }>(basePath, {
        method: "PATCH",
        body: JSON.stringify({ workflow: payload }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-detail", projectSlug, workflowId] })
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
      setIsEditOpen(false)
    },
  })

  const openEditDialog = () => {
    if (!workflow) return
    setEditHours([...(workflow.schedule_hours ?? [])])
    setEditUrls([...(workflow.destination_urls ?? [])])
    setEditEndDate(workflow.recurrence_end_at ? new Date(workflow.recurrence_end_at).toISOString().slice(0, 10) : "")
    const imgs = (workflow.uploaded_images ?? []) as string[]
    setEditImages(imgs.map(url => ({ url, filename: url.split("/").pop() || "image" })))
    setEditNewUrl("")
    setUploadError(null)
    setEditTimezone((workflow.timezone as RecurringTimezone) ?? DEFAULT_TIMEZONE)
    setEditCopyLanguage((workflow.copy_language as CopyLanguage) ?? DEFAULT_COPY_LANGUAGE)
    const cf = workflow.contact_filter as Record<string, unknown> | undefined
    if (cf && (cf.target_type === "segment" || cf.targetType === "segment")) {
      setEditTargetType("segment")
      const ids = (cf.selected_segment_ids ?? cf.selectedSegmentIds ?? []) as number[]
      setEditSegmentIds(ids.map(Number).filter(n => Number.isFinite(n)))
    } else {
      setEditTargetType("all")
      setEditSegmentIds([])
    }
    setIsEditOpen(true)
  }

  const toggleEditHour = (hour: number) => {
    setEditHours(prev =>
      prev.includes(hour) ? prev.filter(h => h !== hour) : [...prev, hour].sort((a, b) => a - b)
    )
  }

  const addEditUrl = () => {
    const url = editNewUrl.trim()
    if (!url || editUrls.includes(url)) return
    setEditUrls(prev => [...prev, url])
    setEditNewUrl("")
  }

  const removeEditUrl = (index: number) => {
    setEditUrls(prev => prev.filter((_, i) => i !== index))
  }

  const handleEditImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    setIsUploadingImage(true)
    setUploadError(null)
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData()
        formData.append('image', file)
        const response = await fetch(`/api/projects/${encodeURIComponent(projectSlug || "")}/workflow_images`, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
        })
        if (response.ok) {
          const data = await response.json()
          setEditImages(prev => [...prev, { url: data.workflow_image.url, filename: data.workflow_image.filename }])
        } else {
          const errData = await response.json().catch(() => null)
          setUploadError(errData?.errors?.join(", ") || `Upload falhou (${response.status})`)
        }
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Erro ao fazer upload")
    } finally {
      setIsUploadingImage(false)
      if (e.target) e.target.value = ''
    }
  }

  const removeEditImage = (index: number) => {
    setEditImages(prev => prev.filter((_, i) => i !== index))
  }

  const handleSaveConfig = () => {
    const payload: Record<string, unknown> = {
      schedule_hours: editHours,
      destination_urls: editUrls,
      uploaded_images: editImages.map(img => img.url),
      timezone: editTimezone,
      copy_language: editCopyLanguage,
      contact_filter: editTargetType === "segment" && editSegmentIds.length > 0
        ? { target_type: "segment", selected_segment_ids: editSegmentIds }
        : {},
    }
    if (editEndDate) {
      payload.recurrence_end_at = editEndDate
    } else {
      payload.recurrence_end_at = null
    }
    updateConfigMutation.mutate(payload)
  }

  const handleAddCopy = () => {
    if (!newCopyTitle.trim() || !newCopyBody.trim()) return
    const updated = [...scheduledCopies, { title: newCopyTitle.trim(), body: newCopyBody.trim() }]
    saveCopiesMutation.mutate(updated)
    setNewCopyTitle("")
    setNewCopyBody("")
    setIsAddCopyOpen(false)
  }

  const handleRemoveCopy = (index: number) => {
    saveCopiesMutation.mutate(scheduledCopies.filter((_, i) => i !== index))
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00")
    return new Intl.DateTimeFormat("pt-BR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return ""
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  }

  const formatHour = (h: number) => `${String(h).padStart(2, "0")}:00`

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !workflow) {
    return (
      <div className="space-y-4">
        <Link to={`/${projectSlug}/broadcasts`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error?.message ?? "Broadcast não encontrado."}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 pb-8">
      {/* Header */}
      <div>
        <Link to={`/${projectSlug}/broadcasts`} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Broadcasts
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{workflow.name}</h1>
              <span className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
                workflow.status === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-700 border-gray-200"
              )}>
                {workflow.status === "active" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                {workflow.status === "active" ? "Ativo" : "Rascunho"}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                <RefreshCw className="h-3 w-3" />
                Recorrente
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {workflow.schedule_hours && workflow.schedule_hours.length > 0 && (
                <span>
                  Horários ({timezoneAbbreviation(workflow.timezone ?? DEFAULT_TIMEZONE)}): {workflow.schedule_hours.map(formatHour).join(", ")}
                </span>
              )}
              <span>Idioma: {copyLanguageLabel(workflow.copy_language)}</span>
              <span>{workflow.dispatch_count ?? 0} envios realizados</span>
              {workflow.destination_urls && (
                <span>{workflow.destination_urls.length} URL{workflow.destination_urls.length !== 1 ? "s" : ""}</span>
              )}
              {(() => {
                const cf = workflow.contact_filter as Record<string, unknown> | undefined
                if (cf && (cf.target_type === "segment" || cf.targetType === "segment")) {
                  const ids = (cf.selected_segment_ids ?? cf.selectedSegmentIds ?? []) as number[]
                  const names = segments.filter(s => ids.includes(s.id)).map(s => s.name)
                  if (names.length > 0) return <span>Segmento: {names.join(", ")}</span>
                }
                return null
              })()}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={openEditDialog}
            >
              <Pencil className="h-4 w-4" />
              Editar
            </Button>
            <Button
              variant={workflow.status === "active" ? "outline" : "default"}
              className="gap-2"
              onClick={() => toggleStatusMutation.mutate(workflow.status === "active" ? "draft" : "active")}
              disabled={toggleStatusMutation.isPending}
            >
              {toggleStatusMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : workflow.status === "active" ? (
                <><PowerOff className="h-4 w-4" />Desativar</>
              ) : (
                <><Power className="h-4 w-4" />Ativar</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Aggregate Metrics */}
      {metrics && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-lg border bg-card p-4 text-center">
            <p className="text-xs text-muted-foreground">Disparos</p>
            <p className="mt-1 text-2xl font-bold">{metrics.total_dispatches}</p>
          </div>
          <div className="rounded-lg border bg-card p-4 text-center">
            <p className="text-xs text-muted-foreground">Audiência</p>
            <p className="mt-1 text-2xl font-bold">{(workflow.estimated_recipients ?? 0).toLocaleString()}</p>
          </div>
          <div className="rounded-lg border bg-blue-50 p-4 text-center">
            <p className="text-xs text-blue-600">Enviados</p>
            <p className="mt-1 text-2xl font-bold text-blue-700">{metrics.sent.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border bg-green-50 p-4 text-center">
            <p className="text-xs text-green-600">Entregues</p>
            <p className="mt-1 text-2xl font-bold text-green-700">{metrics.delivered.toLocaleString()}</p>
            {metrics.sent > 0 && (
              <p className="mt-0.5 text-[10px] text-green-500">{(metrics.delivery_rate * 100).toFixed(1)}%</p>
            )}
          </div>
          <div className="rounded-lg border bg-indigo-50 p-4 text-center">
            <p className="text-xs text-indigo-600">Cliques</p>
            <p className="mt-1 text-2xl font-bold text-indigo-700">{metrics.clicked.toLocaleString()}</p>
            {metrics.delivered > 0 && (
              <p className="mt-0.5 text-[10px] text-indigo-500">{(metrics.click_rate * 100).toFixed(1)}%</p>
            )}
          </div>
          <div className="rounded-lg border bg-card p-4 text-center">
            <p className="text-xs text-muted-foreground">Total msgs</p>
            <p className="mt-1 text-2xl font-bold">{metrics.total_messages.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Scheduled Copies */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold">Próximas copies</h2>
            <p className="text-xs text-muted-foreground">
              {scheduledCopies.length > 0
                ? `${scheduledCopies.length} copy(s) na fila — serão usadas antes da IA gerar`
                : "Nenhuma copy na fila — a IA gera automaticamente"}
            </p>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setIsAddCopyOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Adicionar
          </Button>
        </div>
        {scheduledCopies.length > 0 ? (
          <div className="divide-y">
            {scheduledCopies.map((copy, idx) => (
              <div key={idx} className="flex items-center gap-3 px-5 py-3">
                <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                <div className="min-w-0 flex-1">
                  <span className="mr-2 text-xs font-medium text-muted-foreground">#{idx + 1}</span>
                  <span className="text-sm font-medium">{copy.title}</span>
                  <p className="text-xs text-muted-foreground truncate">{copy.body}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveCopy(idx)}
                  disabled={saveCopiesMutation.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">
            <RefreshCw className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
            A IA gera copies automaticamente a cada envio.
            <br />
            Adicione copies manuais se quiser controlar os próximos envios.
          </div>
        )}
      </div>

      {/* Dispatch History */}
      <div>
        <h2 className="mb-3 text-lg font-semibold">Histórico de envios</h2>
        {history.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed bg-muted/20 p-8 text-center">
            <Send className="mx-auto mb-2 h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">
              Nenhum envio realizado ainda. Ative o broadcast recorrente para começar.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {history.map((day) => (
              <div key={day.date}>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground capitalize">
                  {formatDate(day.date)}
                </h3>
                <div className="space-y-2">
                  {day.dispatches.map((dispatch) => (
                    <div key={dispatch.id} className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/30">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex gap-3">
                          {dispatch.image && (
                            <img
                              src={dispatch.image}
                              alt=""
                              className="h-14 w-14 shrink-0 rounded-md border object-cover"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-muted-foreground">
                                {formatTime(dispatch.started_at)}
                              </span>
                              {dispatch.dispatch_number && (
                                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                                  #{String(dispatch.dispatch_number).padStart(2, "0")}
                                </span>
                              )}
                              {dispatch.status !== "completed" && (
                                <span className={cn(
                                  "rounded px-1.5 py-0.5 text-[10px] font-medium",
                                  dispatch.status === "failed"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-amber-100 text-amber-700"
                                )}>
                                  {dispatch.status === "failed" ? "Falhou" :
                                    dispatch.status === "sending" ? "Enviando..." : "Na fila"}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-sm font-semibold">{dispatch.title || "Sem título"}</p>
                            <p className="text-xs text-muted-foreground">{dispatch.body || ""}</p>
                            {dispatch.url && (
                              <a
                                href={dispatch.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {dispatch.url.length > 60 ? dispatch.url.slice(0, 60) + "..." : dispatch.url}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-3 text-xs">
                          <div className="text-center">
                            <p className="font-bold text-blue-600">{dispatch.metrics.sent.toLocaleString()}</p>
                            <p className="text-muted-foreground">Enviados</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-green-600">{dispatch.metrics.delivered.toLocaleString()}</p>
                            <p className="text-muted-foreground">Entregues</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-indigo-600">{dispatch.metrics.clicked.toLocaleString()}</p>
                            <p className="text-muted-foreground">Cliques</p>
                          </div>
                          <Link
                            to={`/${projectSlug}/broadcasts/${workflowId}/run/${dispatch.id}`}
                            className="ml-1"
                          >
                            <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                              <BarChart3 className="h-3.5 w-3.5" />
                              Análises
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Copy Dialog */}
      <Dialog open={isAddCopyOpen} onOpenChange={setIsAddCopyOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Adicionar copy manual</DialogTitle>
            <DialogDescription>
              Esta copy será usada no próximo envio disponível, antes da IA gerar automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <Input
                placeholder="Título da notificação (ex: [PROMOÇÃO] Só hoje! 🔥)"
                value={newCopyTitle}
                onChange={(e) => setNewCopyTitle(e.target.value)}
                maxLength={50}
                autoFocus
              />
              <p className="mt-1 text-right text-[10px] text-muted-foreground">{newCopyTitle.length}/50</p>
            </div>
            <div>
              <Input
                placeholder="Corpo da notificação (ex: Confira antes que acabe 👇)"
                value={newCopyBody}
                onChange={(e) => setNewCopyBody(e.target.value)}
                maxLength={100}
              />
              <p className="mt-1 text-right text-[10px] text-muted-foreground">{newCopyBody.length}/100</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddCopyOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddCopy} disabled={!newCopyTitle.trim() || !newCopyBody.trim() || saveCopiesMutation.isPending}>
              {saveCopiesMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Config Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar broadcast recorrente</DialogTitle>
            <DialogDescription>
              Altere horários, URLs e configurações. As mudanças valem a partir do próximo ciclo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Fuso horário</Label>
                <Select value={editTimezone} onValueChange={(v) => setEditTimezone(v as RecurringTimezone)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRING_TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Idioma da copy (IA)</Label>
                <Select value={editCopyLanguage} onValueChange={(v) => setEditCopyLanguage(v as CopyLanguage)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COPY_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Schedule Hours */}
            <div className="space-y-2">
              <Label>Horários de envio ({timezoneAbbreviation(editTimezone)})</Label>
              <div className="flex flex-wrap gap-1.5">
                {AVAILABLE_HOURS.map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => toggleEditHour(h)}
                    className={cn(
                      "rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors",
                      editHours.includes(h)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground hover:bg-muted"
                    )}
                  >
                    {String(h).padStart(2, "0")}:00
                  </button>
                ))}
              </div>
              {editHours.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {editHours.length} horário(s) selecionado(s): {editHours.map(h => `${String(h).padStart(2, "0")}:00`).join(", ")}
                </p>
              )}
            </div>

            {/* Destination URLs */}
            <div className="space-y-2">
              <Label>URLs de destino</Label>
              {editUrls.length > 0 && (
                <div className="space-y-1.5">
                  {editUrls.map((url, idx) => (
                    <div key={idx} className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                      <span className="min-w-0 flex-1 truncate text-xs font-mono">{url}</span>
                      <button
                        type="button"
                        onClick={() => removeEditUrl(idx)}
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="https://exemplo.com/pagina"
                  value={editNewUrl}
                  onChange={(e) => setEditNewUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEditUrl() } }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEditUrl}
                  disabled={!editNewUrl.trim()}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label>Imagens (rotação)</Label>
              {editImages.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editImages.map((img, idx) => (
                    <div key={idx} className="group relative">
                      <img
                        src={img.url}
                        alt={img.filename}
                        className="h-16 w-16 rounded-md border object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeEditImage(idx)}
                        className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted">
                  {isUploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4" />
                  )}
                  {isUploadingImage ? "Enviando..." : "Adicionar imagens"}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleEditImageUpload}
                    disabled={isUploadingImage}
                  />
                </label>
                {uploadError && (
                  <p className="mt-1 text-xs text-destructive">{uploadError}</p>
                )}
              </div>
            </div>

            {/* Recurrence End Date */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                Data de encerramento
              </Label>
              <Input
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {editEndDate ? `Encerra em ${new Date(editEndDate + "T00:00:00").toLocaleDateString("pt-BR")}` : "Sem data de encerramento — roda indefinidamente"}
              </p>
            </div>

            {/* Segment Targeting */}
            <div className="space-y-2">
              <Label>Público-alvo</Label>
              <Select
                value={editTargetType}
                onValueChange={(v: "all" | "segment") => {
                  setEditTargetType(v)
                  if (v === "all") setEditSegmentIds([])
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os inscritos</SelectItem>
                  <SelectItem value="segment">Segmento específico</SelectItem>
                </SelectContent>
              </Select>
              {editTargetType === "segment" && (
                <div className="rounded-md border p-3 space-y-2">
                  {segmentsQuery.isLoading ? (
                    <p className="text-xs text-muted-foreground">Carregando segmentos...</p>
                  ) : segments.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum segmento criado. Crie na página Segmentos.</p>
                  ) : (
                    segments.map(seg => (
                      <label key={seg.id} className="flex items-center gap-2 text-xs">
                        <Checkbox
                          checked={editSegmentIds.includes(seg.id)}
                          onCheckedChange={(checked) => {
                            if (checked === true) {
                              setEditSegmentIds(prev => [...new Set([...prev, seg.id])])
                            } else {
                              setEditSegmentIds(prev => prev.filter(id => id !== seg.id))
                            }
                          }}
                        />
                        {seg.name}
                        {seg.subscriber_count != null && (
                          <span className="text-muted-foreground">({seg.subscriber_count.toLocaleString()})</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveConfig}
              disabled={editHours.length === 0 || editUrls.length === 0 || updateConfigMutation.isPending}
            >
              {updateConfigMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RecurringBroadcastDetail
